import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, Copy, Loader2, Mail, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import { cn } from "@/lib/utils";

export interface OrgInvite {
  id: number;
  invite_token: string;
  invitee_email: string;
  invited_role: string;
  status: string;
  expires_at: string;
  created_at: string;
  user_profiles?: {
    first_name: string;
    last_name: string;
  } | null;
}

function inviterName(invite: OrgInvite): string {
  return `${invite.user_profiles?.first_name ?? ""} ${invite.user_profiles?.last_name ?? ""}`.trim();
}

function relative(date: string): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

/** Empty state for an org that has never sent an invitation. */
export function InvitationsEmptyState() {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <Mail className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="mt-4 font-semibold">{t.settings.invitations.empty}</p>
      <p className="mt-1 max-w-xs text-sm text-muted-foreground">
        {t.settings.invitations.emptyDesc}
      </p>
    </div>
  );
}

/**
 * Header of an invitation group: "Pending · 3" / "Expired · 2", with the group's
 * bulk action (if any) aligned right. Pending reads amber (still actionable),
 * expired grey (dead links) — never brand orange, which stays reserved for the
 * primary action on the page.
 */
function InviteGroupHeader({
  label,
  count,
  tone,
  action,
}: {
  label: string;
  count: number;
  tone: "pending" | "expired";
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-2">
      <span
        className={cn(
          "text-sm font-medium",
          tone === "pending" ? "text-amber-700" : "text-muted-foreground"
        )}
      >
        {label} · {count}
      </span>
      {action}
    </div>
  );
}

/** One invitation as a flat, hairline-separated row — no card. */
function InviteRow({
  invite,
  meta,
  dimmed,
  actions,
}: {
  invite: OrgInvite;
  meta: string;
  dimmed?: boolean;
  actions: ReactNode;
}) {
  const roleLabel = useRoleLabel();

  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <span className={cn("truncate font-medium", dimmed && "text-muted-foreground")}>
            {invite.invitee_email}
          </span>
          {/* The invitee's role. In the metadata line below it read as the
              inviter's role. */}
          <Badge
            variant="outline"
            className={cn("shrink-0 font-normal", dimmed && "text-muted-foreground")}
          >
            {roleLabel(invite.invited_role)}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{meta}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">{actions}</div>
    </div>
  );
}

function ResendButton({
  invite,
  resending,
  onResend,
}: {
  invite: OrgInvite;
  resending: boolean;
  onResend: (invite: OrgInvite) => void;
}) {
  const { t } = useLanguage();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground"
      onClick={() => onResend(invite)}
      disabled={resending}
    >
      {resending ? (
        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="mr-1 h-4 w-4" />
      )}
      {t.settings.invitations.resend}
    </Button>
  );
}

/** Copies the invitation link, and reads back "copied" until the next one. */
function CopyLinkButton({
  invite,
  copied,
  onCopyLink,
}: {
  invite: OrgInvite;
  copied: boolean;
  onCopyLink: (invite: OrgInvite) => void;
}) {
  const { t } = useLanguage();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground"
      onClick={() => onCopyLink(invite)}
    >
      {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
      {t.settings.invitations.copyLink}
    </Button>
  );
}

/** Revoking is destructive, so it sits behind the overflow menu. */
function RevokeInviteMenu({
  invite,
  onRevoke,
}: {
  invite: OrgInvite;
  onRevoke: (invite: OrgInvite) => void;
}) {
  const { t } = useLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          aria-label={t.settings.invitations.moreActions}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => onRevoke(invite)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t.settings.invitations.revoke}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Copy the (still live) link, resend it, or revoke the invitation. */
function PendingInviteActions({
  invite,
  handlers,
}: {
  invite: OrgInvite;
  handlers: InviteActionHandlers;
}) {
  return (
    <>
      <CopyLinkButton
        invite={invite}
        copied={handlers.copiedId === invite.id}
        onCopyLink={handlers.onCopyLink}
      />
      <ResendButton
        invite={invite}
        resending={handlers.isResending(invite)}
        onResend={handlers.onResend}
      />
      <RevokeInviteMenu invite={invite} onRevoke={handlers.onRevoke} />
    </>
  );
}

/** An expired link is dead: it can only be resent (fresh link) or deleted. */
function ExpiredInviteActions({
  invite,
  handlers,
}: {
  invite: OrgInvite;
  handlers: InviteActionHandlers;
}) {
  const { t } = useLanguage();

  return (
    <>
      <ResendButton
        invite={invite}
        resending={handlers.isResending(invite)}
        onResend={handlers.onResend}
      />
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={() => handlers.onDelete(invite)}
        aria-label={t.settings.invitations.deleteInviteTitle}
      >
        <Trash2 className="mr-1 h-4 w-4" />
        {t.common.delete}
      </Button>
    </>
  );
}

export interface InviteActionHandlers {
  onCopyLink: (invite: OrgInvite) => void;
  onResend: (invite: OrgInvite) => void;
  onRevoke: (invite: OrgInvite) => void;
  onDelete: (invite: OrgInvite) => void;
  onClearExpired: () => void;
  isResending: (invite: OrgInvite) => boolean;
  copiedId: number | null;
  clearingExpired: boolean;
}

/** Pending invites are actionable: copy the link, resend it, or revoke it. */
export function PendingInviteGroup({
  invites,
  handlers,
}: {
  invites: OrgInvite[];
  handlers: InviteActionHandlers;
}) {
  const { t } = useLanguage();

  return (
    <div>
      <InviteGroupHeader
        label={t.settings.invitations.pending}
        count={invites.length}
        tone="pending"
      />
      <div className="divide-y divide-border">
        {invites.map((invite) => (
          <InviteRow
            key={invite.id}
            invite={invite}
            meta={[
              t.settings.invitations.invitedRelativeBy
                .replace("{time}", relative(invite.created_at))
                .replace("{name}", inviterName(invite)),
              t.settings.invitations.expiresIn.replace("{time}", relative(invite.expires_at)),
            ].join(" · ")}
            actions={<PendingInviteActions invite={invite} handlers={handlers} />}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Expired invites are dimmed and offer no Copy link — the link behind them is
 * dead. They can only be resent (which mints a fresh link and expiry) or deleted.
 */
export function ExpiredInviteGroup({
  invites,
  handlers,
}: {
  invites: OrgInvite[];
  handlers: InviteActionHandlers;
}) {
  const { t } = useLanguage();

  return (
    <div>
      <InviteGroupHeader
        label={t.settings.invitations.expired}
        count={invites.length}
        tone="expired"
        action={
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-destructive"
            onClick={handlers.onClearExpired}
            disabled={handlers.clearingExpired}
          >
            {handlers.clearingExpired ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t.settings.invitations.clearAllExpired}
          </Button>
        }
      />
      <div className="divide-y divide-border">
        {invites.map((invite) => (
          <InviteRow
            key={invite.id}
            invite={invite}
            dimmed
            meta={[
              t.settings.invitations.expiredAgo.replace("{time}", relative(invite.expires_at)),
              t.settings.invitations.invitedByName.replace("{name}", inviterName(invite)),
            ].join(" · ")}
            actions={<ExpiredInviteActions invite={invite} handlers={handlers} />}
          />
        ))}
      </div>
    </div>
  );
}
